import { useEffect, useState } from "react";
import FeedbackComment from "pages/conductLegalResearch/fragments/feedBackComment";
import ResponseInput from "pages/conductLegalResearch/fragments/responseInput";
import SectionHeader from "pages/conductLegalResearch/fragments/sectionHeader";
import { formatDate } from "utils/helpers";
import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import EvaluationModal from "components/EvaluationModal";

interface Feedback {
    id: number;
    research_request: number;
    given_by: string;
    role: string;
    feedback_type: string;
    note: string;
    status: string;
}

// Helper: get initials from name
const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export default function MainRightPanel({ request }: { request: any }) {
    const { t } = useTranslation();
    const contextData = useAuth();
    const [formState, setFormState] = useState({ ...request });
    const [feedbacks, setFeedbacks] = useState<Feedback[]>(formState?.feedbacks || []);
    const [respondbackFeedbacks, setRespondbackFeedback] = useState<Feedback[]>(request?.respond_back_feedbacks || []);
    const [state, setState] = useState("feedback");
    const [showEvaluation, setShowEvaluation] = useState(false);
    useEffect(() => { }, [formState])

    const canEvaluate =
        contextData?.user?.user_type !== "external" &&
        (request?.prosecutor_assignments?.length ?? 0) > 0 &&
        contextData?.user?.roles?.some(
            (role: string) =>
                role === "LegalResearchCoordinator" || role === "LRDIFUDDirector"
        );
    const bearer = contextData.token || contextData?.user?.access_token || "";
    const evaluatedBy =
        contextData?.user?.full_name ||
        contextData?.user?.first_name ||
        contextData?.user?.user_id;

    return (
        <div className="overflow-y-scroll scrollbar-hide flex w-[45%] flex-col bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-4 items-center pb-2 mb-4 border-b-2">
                <p onClick={() => setState("feedback")} className={`cursor-pointer font-semibold ${state === "feedback" ? "text-base text-white bg-black p-2 rounded-lg" : "text-base text-gray-400"}`}>
                    {t("FeedbackSection") || "Feedback Section"}
                </p>
                <p onClick={() => setState("respondback")} className={`cursor-pointer font-semibold ${state === "respondback" ? "text-base text-white bg-black p-2 rounded-lg" : "text-base text-gray-400"}`}>
                    {t("RespondBackSection") || "Respond Back Section"}
                </p>
                {canEvaluate && (
                    <button
                        onClick={() => setShowEvaluation(true)}
                        className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-[#9B0505] border border-[#9B0505] rounded-lg px-3 py-1.5 hover:bg-[#9B0505] hover:text-white transition"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {t("EvaluateTeam") || "Evaluate Team"}
                    </button>
                )}
            </div>
            {showEvaluation && (
                <EvaluationModal
                    requestId={request.id}
                    teamMembers={request?.prosecutor_assignments || []}
                    evaluatedBy={evaluatedBy}
                    bearer={bearer}
                    onClose={() => setShowEvaluation(false)}
                />
            )}
            {/* Feedback Comments */}
            <div className="overflow-y-scroll scrollbar-hide flex-1 space-y-6 mb-6">
                {state === "feedback" ? (
                    <>
                        {feedbacks.map((feedback: any) => (
                            <FeedbackComment
                                key={feedback.id}
                                name={feedback.given_by}
                                role={feedback.role}
                                commentedDate={formatDate(feedback.created_at)}
                                comment={feedback.note}
                            />
                        ))}
                        {feedbacks.length === 0 && (
                            <p className="text-center my-auto">{t("NoMessagesYet") || "No Messages Yet"}</p>
                        )}
                    </>
                ) : (
                    <>
                        {respondbackFeedbacks.length === 0 && (
                            <p className="text-center my-auto">{t("NoRespondBackFeedbackYet") || "No Respond Back Feedback Yet"}</p>
                        )}
                        {respondbackFeedbacks?.slice().reverse().map((feedback: any) => {
                            if (feedback.feedback_type === "stakeholder_request") return null;

                            // Collect all file arrays from the feedback object
                            // The API may return files under different keys depending on the backend
                            const files: any[] = [
                                ...(feedback.uploaded_files || []),
                                ...(feedback.respond_back_feedback_files || []),
                                ...(feedback.files || []),
                            ];

                            return (
                                <div key={feedback.id} className="items-start bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="flex">
                                        {/* Avatar */}
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0 mr-3 bg-[#AA232F]">
                                            {getInitials(feedback.given_by)}
                                        </div>
                                        <div>
                                            <div className="font-semibold text-gray-800">{feedback.given_by}</div>
                                            <div className="text-sm text-gray-500">{feedback.role}</div>
                                            <div className="flex gap-2">
                                                <div className="text-xs text-red-500 mb-2">{t("CommentedDate") || "Commented Date"}</div>
                                                <div className="text-xs text-gray-500 mb-2">{formatDate(feedback.created_at)}</div>
                                            </div>
                                            <p className="text-gray-700 text-sm">{feedback.note}</p>
                                        </div>
                                        {feedback.feedback_type && (
                                            <div className="ml-auto bg-gray-200 h-fit px-2 py-1 font-semibold text-sm text-gray-800 rounded-lg">
                                                <p>{feedback.feedback_type}</p>
                                            </div>
                                        )}
                                    </div>
                                    {/* Files — render all files attached to this respond-back entry */}
                                    {files.length > 0 && (
                                        <div className="pl-12 mt-3">
                                            <DocumentViewerModal
                                                documents={files.map((f: any) => ({
                                                    uu_id: f.uu_id || f.id || "",
                                                    document_type: f.document_type || f.file_type || "respond_back_feedback_files",
                                                    document_uuid: f.document_uuid || f.file_uuid || f.uu_id || f.id || "",
                                                    document_path: f.document_path || f.file_path || f.file || "",
                                                    created_at: f.created_at || new Date().toISOString(),
                                                    updated_at: f.updated_at || new Date().toISOString(),
                                                }))}
                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}accuracy_government_sector/directive-accuracy/respond-back-feedback-files/`}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            {/* Write Back Response */}
            {state === "feedback" &&
                <ResponseInput request={request} onSubmit={setFeedbacks} type="sector" />}
        </div>
    );
}